import React, { useState } from 'react';
import { X, Mic } from 'lucide-react';

interface StartVoiceConversationModalProps {
  avatarName: string;
  onStart: (opts: { context?: string; openingLine?: string }) => void;
  onClose: () => void;
}

/**
 * Both fields are optional. With neither, the avatar runs on its own persona
 * and its configured greeting, which is the same conversation the robots have.
 */
export function StartVoiceConversationModal({ avatarName, onStart, onClose }: StartVoiceConversationModalProps) {
  const [context, setContext] = useState('');
  const [openingLine, setOpeningLine] = useState('');

  const handleStart = () => {
    onStart({
      context: context.trim() || undefined,
      openingLine: openingLine.trim() || undefined,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0] flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary">
              <Mic size={16} />
            </div>
            <h2 className="text-base font-bold text-navy">Talk to {avatarName}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
          <p className="text-sm text-slate-500">
            Uses your microphone and speakers. Both fields below are optional.
          </p>

          <div>
            <label className="block text-sm font-semibold text-navy mb-2">What should this conversation focus on?</label>
            <textarea
              value={context}
              onChange={(e) => setContext(e.target.value)}
              rows={3}
              placeholder="e.g. Challenge our pricing assumptions for the mid-market tier."
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
            />
            <p className="mt-2 text-xs text-slate-400">Added to the avatar's existing persona, not replacing it.</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-navy mb-2">Opening line</label>
            <input
              value={openingLine}
              onChange={(e) => setOpeningLine(e.target.value)}
              placeholder="e.g. Right, let's talk about why this pricing worries me."
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
            />
            <p className="mt-2 text-xs text-slate-400">Leave blank and the avatar opens with its usual greeting.</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#e2e8f0] bg-slate-50/50 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex items-center gap-1.5 rounded-xl h-9 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleStart}
            className="flex items-center gap-1.5 rounded-xl h-9 px-5 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm"
          >
            <Mic size={14} /> Start talking
          </button>
        </div>
      </div>
    </div>
  );
}

export default StartVoiceConversationModal;
